import { action } from 'satcheljs';
import type { ActionMessage } from 'satcheljs';
import type { MailboxInfo } from 'owa-client-types';

export interface OnBriefingMeetingSentActionMessage extends ActionMessage {
    itemId: string;
    mailboxInfo: MailboxInfo;
    briefingId?: string;
}

export interface OnBriefingMeetingHandedOffActionMessage extends ActionMessage {
    itemId: string;
    mailboxInfo: MailboxInfo;
    briefingId?: string;
}

/**
 * Fired when the user commits a meeting from the MyOutlook simplified event form.
 * The PersonalOS store listens and marks the originating item done via
 * `PATCH /pos/briefing/actions {op:'done'}`. `itemId` is the D2
 * briefing-projection id; `briefingId` identifies the briefing that displayed it.
 */
export const onBriefingMeetingSent: (
    itemId: string,
    mailboxInfo: MailboxInfo,
    briefingId?: string
) => OnBriefingMeetingSentActionMessage = action(
    'onBriefingMeetingSent',
    (itemId: string, mailboxInfo: MailboxInfo, briefingId?: string) => ({
        itemId,
        mailboxInfo,
        briefingId,
    })
);

/**
 * Fired when the user pops the simplified event form out to full compose. The item is
 * not done yet -- the store remembers it as pending and only completes it if the
 * popped-out form actually commits an event.
 */
export const onBriefingMeetingHandedOff: (
    itemId: string,
    mailboxInfo: MailboxInfo,
    briefingId?: string
) => OnBriefingMeetingHandedOffActionMessage = action(
    'onBriefingMeetingHandedOff',
    (itemId: string, mailboxInfo: MailboxInfo, briefingId?: string) => ({
        itemId,
        mailboxInfo,
        briefingId,
    })
);

/**
 * Fired by the calendar compose form when it successfully commits an event whose entry
 * source is `PersonalOS_ScheduleMeeting`. Deliberately carries no PersonalOS state so
 * the calendar side stays ignorant of briefing concepts -- the store correlates it with
 * the item stashed by `onBriefingMeetingHandedOff`.
 */
export const onPersonalOsComposeCommitted: () => ActionMessage = action(
    'onPersonalOsComposeCommitted'
);
